(() => {
  if (customElements.get('toba-bundle-buy')) return;
  class BundleMessageError extends Error {}
  class TobaBundleBuy extends HTMLElement {
    connectedCallback() {
      if (this.attached) return;
      this.attached = true;
      this.busy = false;
      this.locked = false;
      this.rows = Array.from(this.querySelectorAll('[data-bundle-row]'));
      this.add = this.querySelector('[data-bundle-add]');
      this.addLabel = this.add.textContent;
      this.querySelector('form').addEventListener('submit', event => this.submit(event));
      this.addEventListener('change', () => this.sync());
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-bundle-step]');
        if (!button || !this.contains(button) || button.disabled || this.busy || this.locked) return;
        const row = button.closest('[data-bundle-row]');
        const select = row.querySelector('[data-bundle-quantity]');
        select.value = String(Math.max(0, Math.min(Number(row.dataset.maximum), Number(select.value) + Number(button.dataset.bundleStep))));
        this.sync();
      });
      this.sync();
    }

    sync() {
      let total = 0;
      let selected = 0;
      for (const row of this.rows) {
        const select = row.querySelector('[data-bundle-quantity]');
        const maximum = Number(row.dataset.maximum);
        if (Number(select.value) > maximum || !Number.isSafeInteger(Number(select.value))) select.value = '0';
        const quantity = Number(select.value);
        select.disabled = this.busy || this.locked || maximum === 0;
        for (const option of select.options) option.disabled = Number(option.value) > maximum;
        for (const button of row.querySelectorAll('[data-bundle-step]')) {
          button.disabled = this.busy || this.locked || (Number(button.dataset.bundleStep) > 0 ? quantity >= maximum : quantity <= 0);
        }
        total += quantity * Number(row.dataset.price);
        selected += quantity;
      }
      this.querySelector('[data-bundle-total]').textContent = new Intl.NumberFormat(this.dataset.locale || 'ja', { style: 'currency', currency: this.dataset.currency || 'JPY' }).format(total / 100);
      this.add.disabled = this.busy || this.locked || selected === 0;
      this.add.textContent = this.busy ? this.dataset.addingLabel : this.addLabel;
      this.setAttribute('aria-busy', String(this.busy));
    }

    async getCart() {
      const response = await fetch(this.dataset.cartUrl + '.js', { cache: 'no-store' });
      if (!response.ok) throw new BundleMessageError(this.dataset.requestError);
      return response.json();
    }

    async getAvailability() {
      return Promise.all(this.rows.map(async row => {
        if (Number(row.querySelector('[data-bundle-quantity]').value) === 0 ||
          (!row.dataset.productId && !row.dataset.variantId && Number(row.dataset.maximum) === 0)) {
          return { kind: row.dataset.kind, productId: row.dataset.productId, variantId: row.dataset.variantId,
            maximum: Number(row.dataset.maximum), price: Number(row.dataset.price),
            priceText: row.querySelector('.toba-bundle-price').textContent,
            availabilityText: row.querySelector('.toba-bundle-availability').textContent };
        }
        const url = new URL(row.dataset.availabilityUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new BundleMessageError(this.dataset.requestError);
        url.searchParams.set('view', 'toba-availability');
        url.searchParams.set('variant', row.dataset.variantId);
        const response = await fetch(url.pathname + url.search, { cache: 'no-store' });
        if (!response.ok) throw new BundleMessageError(this.dataset.requestError);
        const state = await response.json();
        if (state?.version !== 1 || state.productId !== row.dataset.productId || state.variantId !== row.dataset.variantId ||
          !Number.isSafeInteger(state.maximum) || state.maximum < 0 ||
          !Number.isSafeInteger(state.price) || state.price < 0 ||
          typeof state.priceText !== 'string' || typeof state.availabilityText !== 'string') {
          throw new BundleMessageError(this.dataset.requestError);
        }
        return { ...state, kind: row.dataset.kind };
      }));
    }

    prepare(cart, availability) {
      const items = [];
      let invalid = false;
      for (const row of this.rows) {
        const fresh = availability.find(item => item.kind === row.dataset.kind);
        if (!fresh || fresh.productId !== row.dataset.productId || fresh.variantId !== row.dataset.variantId) throw new BundleMessageError(this.dataset.limitError);
        const inCart = cart.items.filter(item => String(item.product_id) === row.dataset.productId).reduce((sum, item) => sum + item.quantity, 0);
        const maximum = Math.max(0, Math.min(fresh.maximum, Number(row.dataset.limit) - inCart));
        const select = row.querySelector('[data-bundle-quantity]');
        const quantity = Number(select.value);
        row.dataset.maximum = String(maximum);
        row.dataset.price = String(fresh.price);
        row.querySelector('.toba-bundle-price').textContent = fresh.priceText;
        row.querySelector('.toba-bundle-availability').textContent = fresh.availabilityText;
        if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > maximum) {
          select.value = '0';
          invalid = true;
        } else if (quantity > 0) {
          items.push({ id: row.dataset.variantId, quantity });
        }
      }
      this.sync();
      if (invalid) throw new BundleMessageError(this.dataset.limitError);
      if (!items.length) throw new BundleMessageError(this.dataset.emptyError);
      return items;
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || this.locked) return;
      const error = this.querySelector('[data-bundle-error]');
      error.hidden = true;
      this.busy = true;
      this.sync();
      let attempted = false;
      try {
        const [before, availability] = await Promise.all([this.getCart(), this.getAvailability()]);
        const items = this.prepare(before, availability);
        attempted = true;
        const response = await fetch(this.dataset.addUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) throw new BundleMessageError(result.description || this.dataset.requestError);
        const after = await this.getCart();
        for (const item of items) {
          const quantityFor = cart => cart.items.filter(line => String(line.variant_id) === String(item.id)).reduce((sum, line) => sum + line.quantity, 0);
          if (quantityFor(after) < quantityFor(before) + item.quantity) throw new BundleMessageError(this.dataset.resultError);
        }
        this.locked = true;
        window.location.assign(this.dataset.cartUrl);
      } catch (failure) {
        if (attempted) this.locked = true;
        console.error('[toba-bundle-buy]', failure);
        const message = failure instanceof BundleMessageError && failure.message ? failure.message : this.dataset.requestError;
        error.textContent = message + (attempted && message !== this.dataset.resultError ? ' ' + this.dataset.resultError : '');
        error.hidden = false;
      } finally {
        this.busy = false;
        this.sync();
      }
    }
  }
  customElements.define('toba-bundle-buy', TobaBundleBuy);
})();
